import { Trans, useLingui } from '@lingui/react/macro'
import {
  Box,
  Button,
  Card,
  Group,
  List,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconAlertTriangle, IconPhone, IconSparkles } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import { propertyHooks } from '$src/lib/trpcHooks'

interface EmergencyWaterDamageCardProps {
  isPremium: boolean
}

const EMERGENCY_PHONE = '020 7484 00'
const EMERGENCY_PHONE_LINK = 'tel:+358207484000'

export function EmergencyWaterDamageCard({
  isPremium,
}: EmergencyWaterDamageCardProps) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { data: propertiesResponse } = propertyHooks.useList()

  const openEmergencyModal = () => {
    modals.open({
      title: t`Water damage emergency help`,
      centered: true,
      size: 'lg',
      children: (
        <Stack gap="lg">
          <Stack gap="xs">
            <Text size="lg" fw={700}>
              <Trans>1. Close your apartment's water shutoff valve</Trans>
            </Text>
            <Text size="sm">
              <Trans>
                The apartment's water shutoff valve is usually found in your
                apartment's utility room near the main water meter.
              </Trans>
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              <Trans>2. Call the emergency hotline at 020 7484 00</Trans>
            </Text>
            <Text size="sm">
              <Trans>
                The call is directed to our partner Polygon's emergency service,
                which is available 24/7. In addition to water damage, you can
                report minor fire damage, such as when smoke has entered the
                apartment.
              </Trans>
            </Text>
            <Button
              component="a"
              href={EMERGENCY_PHONE_LINK}
              size="lg"
              color="red"
              variant="filled"
              fullWidth
              leftSection={<IconPhone size={20} />}
            >
              {EMERGENCY_PHONE}
            </Button>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              <Trans>
                3. Mention at the start of the call that you are a Markku.fi
                customer. Explain what happened and follow the instructions
                given to you.
              </Trans>
            </Text>
            <Text size="sm">
              <Trans>
                The customer relationship is established between you and Polygon
                during the call. They will bill you directly for the emergency
                visit and damage prevention. We have negotiated the discounted
                prices shown below for Markku customers.
              </Trans>
            </Text>
            <Text size="sm" fw={600} mt="xs">
              <Trans>Markku customer prices (incl. VAT 25.5%):</Trans>
            </Text>
            <Table withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Trans>Service</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Price</Trans>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>
                    <Trans>Damage prevention (JVT)</Trans>
                  </Table.Td>
                  <Table.Td>68,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Trans>Special cleaning</Trans>
                  </Table.Td>
                  <Table.Td>68,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Trans>Moisture/fire damage assessment</Trans>
                  </Table.Td>
                  <Table.Td>76,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Trans>Project management</Trans>
                  </Table.Td>
                  <Table.Td>80,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Trans>Indoor air specialist</Trans>
                  </Table.Td>
                  <Table.Td>94,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={600}>
                    <Trans>Emergency call fee</Trans>
                  </Table.Td>
                  <Table.Td fw={600}>
                    <Trans>135,00 € / pcs</Trans>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            <Text size="xs" c="dimmed" mt="xs">
              <Trans>
                Additional charges: mileage compensation €1.65/km (car/van),
                €2.40/km (truck), materials at Polygon list price -25% +
                material surcharge 12%, HVAC work and subcontracting +12%.
              </Trans>
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              <Trans>
                Overtime and additional fees: Overtime is charged according to
                actual hours with percentage increases per the Technology
                Industry Workers' Collective Agreement. Holiday eve +100%
                (previous weekday 18:00 – next day 06:00), Public holiday +100%
                (previous weekday/Saturday 18:00 – next weekday 06:00).
              </Trans>
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              <Trans>
                Note: If the work is covered by an insurance company, the prices
                agreed between Polygon and the insurance company apply.
              </Trans>
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              <Trans>4. File a claim with your insurance company</Trans>
            </Text>
            <Text size="sm">
              <Trans>
                In many cases, the insurance company will reimburse the costs of
                damage prevention, but each case is handled individually.
              </Trans>
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              <Trans>
                5. Add the damage report from Polygon to your Markku maintenance
                log.
              </Trans>
            </Text>
          </Stack>
        </Stack>
      ),
    })
  }

  const openSubscriptionModal = () => {
    const firstProperty = propertiesResponse?.data[0]

    modals.open({
      title: t`Water damage emergency help`,
      centered: true,
      children: (
        <Stack gap="lg">
          <Text>
            <Trans>
              Water damage doesn't choose a time. We are there when you need us.
            </Trans>
          </Text>

          <List
            spacing="xs"
            size="sm"
            icon={
              <IconSparkles
                size={16}
                color="var(--mantine-color-brandOrange-6)"
              />
            }
          >
            <List.Item>
              <Trans>24/7 service - including Christmas and Midsummer</Trans>
            </List.Item>
            <List.Item>
              <Trans>Professionals on site quickly</Trans>
            </List.Item>
            {/*<List.Item>Yhteistyössä vakuutusyhtiöiden kanssa</List.Item>*/}
            <List.Item>
              <Trans>
                Water damage emergency service included in all Markku packages
              </Trans>
            </List.Item>
          </List>

          <Button
            fullWidth
            color="brandOrange"
            onClick={() => {
              modals.closeAll()
              if (firstProperty) {
                navigate({
                  to: '/properties/$propertyId',
                  params: { propertyId: String(firstProperty.id) },
                  search: { tab: 'package' },
                })
              }
            }}
          >
            <Trans>Explore Markku packages</Trans>
          </Button>
        </Stack>
      ),
    })
  }

  // TODO: disabled for now, because navigating to propert/$propertyId doesn't work if user doesn't have property
  // which is the case when app is opened first time
  if (!isPremium) {
    return null
  }

  return (
    <Card
      radius="md"
      withBorder
      p="sm"
      bg={isPremium ? undefined : 'orange.0'}
      onClick={isPremium ? openEmergencyModal : openSubscriptionModal}
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        opacity: isPremium ? 1 : 0.85,
        position: 'relative',
      }}
      styles={{
        root: {
          '&:hover': {
            transform: 'translateX(4px)',
            borderColor: isPremium
              ? 'var(--mantine-color-red-3)'
              : 'var(--mantine-color-brandOrange-4)',
          },
        },
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--mantine-color-red-0)',
              flexShrink: 0,
            }}
          >
            <IconAlertTriangle size={20} color="var(--mantine-color-red-7)" />
          </Box>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text fw={600}>
              <Trans>Water damage emergency 24/7</Trans>
            </Text>
            <Text c="dimmed" size="sm">
              {isPremium
                ? t`24-hour service`
                : t`Peace of mind around the clock`}
            </Text>
          </Stack>
        </Group>
        <Box style={{ flexShrink: 0 }}>
          {isPremium ? (
            <IconPhone size={18} color="var(--mantine-color-gray-5)" />
          ) : (
            <IconSparkles
              size={18}
              color="var(--mantine-color-brandOrange-6)"
            />
          )}
        </Box>
      </Group>
    </Card>
  )
}
